import { useState, useEffect } from 'react';
import { useTenant } from '@tectonics/commons/tenant';
import { useEnrollment } from '../context';
import { pollEnrollmentStatus } from '../services/enrollment-api';

export interface PrefillData {
  readonly bankId: string | null | undefined;
  readonly identification: string | null | undefined;
  readonly omitCpfField: boolean;
  readonly omitBankField: boolean;
  readonly loading: boolean;
}

const PREFILL_TIMEOUT_MS = 10000;

export function useEnrollmentPrefill(): PrefillData {
  const tenant = useTenant();
  const { data } = useEnrollment();
  const [bankId, setBankId] = useState<string | null | undefined>(null);
  const [identification, setIdentification] = useState<string | null | undefined>(null);
  const [omitCpfField, setOmitCpfField] = useState<boolean>(false);
  const [omitBankField, setOmitBankField] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    async function fetchPrefill() {
      try {
        const result = await Promise.race([
          pollEnrollmentStatus(data.contract, tenant.code),
          new Promise<never>((_, reject) => {
            timeoutId = setTimeout(() => reject(new Error('Prefill query timed out.')), PREFILL_TIMEOUT_MS);
          }),
        ]);

        if (cancelled) return;

        const enrollment = result.enrollment;
        if (enrollment) {
          setOmitCpfField(enrollment.omit_cpf_field === true);
          setOmitBankField(enrollment.omit_bank_field === true);
          const bankCode = enrollment.bank?.code;
          if (bankCode) setBankId(bankCode);
          if (enrollment.identification) {
            setIdentification(enrollment.identification);
          }
        }
      } catch (error: unknown) {
        if (!cancelled) {
          // eslint-disable-next-line no-console
          console.error('Failed to fetch enrollment prefill:', error);
        }
      } finally {
        clearTimeout(timeoutId);
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchPrefill();

    return () => { cancelled = true; };
  }, [data.contract, tenant.code]);

  return { bankId, identification, omitCpfField, omitBankField, loading };
}
